import { Link } from 'react-router-dom';
import { Play, Plus, Check } from 'lucide-react';
import { SmartImage } from '@/shared/ui/SmartImage';
import type { Movie } from '@/shared/types';
import { useStore } from '@/shared/state/store';
import { cn } from '@/shared/utils/cn';

/**
 * Movieflix home hero — visually DISTINCT from MovieDetail's hero so the
 * two pages don't feel like duplicates:
 *
 *   - MovieDetail: a single isolated poster floating on blurred bg.
 *   - HeroBillboard (this): a 3-poster "fan stack" — featured movie's
 *     poster centered + sharp + the next two trending posters peeking
 *     from behind, rotated and dimmed. Plus a "TONIGHT'S PICK" label
 *     band above. Communicates editorial pick / curated recommendation
 *     rather than "you're reading about one movie".
 *
 * The featured poster's size is unchanged from v8.5 (w-[44%] / max 220 px /
 * 2:3). Only the surrounding layout changes.
 */
export function HeroBillboard({
  movie,
  peekLeft,
  peekRight,
}: {
  movie: Movie;
  peekLeft?: Movie;
  peekRight?: Movie;
}) {
  const inList = useStore((s) => s.myListMovieIds.has(movie.id));
  const toggle = useStore((s) => s.toggleMyList);
  return (
    <div
      data-testid="hero-billboard"
      data-movie-id={movie.id}
      className="relative w-full h-[68vh] -mt-16 mb-6 overflow-hidden"
    >
      {/* Blurred backdrop — same poster, scaled past edges so the blur
          doesn't reveal a sharp seam. Dimmed for foreground legibility. */}
      <SmartImage
        fill
        src={movie.poster}
        alt=""
        sizes="100vw"
        className="h-full"
        style={{
          filter: 'blur(40px) brightness(0.45) saturate(1.1)',
          transform: 'scale(1.2)',
        }}
      />
      {/* Vertical fade-to-velvet so the title block reads cleanly. */}
      <div className="absolute inset-0 bg-gradient-to-b from-velvet/40 via-velvet/10 to-velvet" />

      {/* Editorial "TONIGHT'S PICK" band — flanked by hairlines. */}
      <div className="absolute top-20 left-0 right-0 flex items-center justify-center gap-3 pointer-events-none">
        <span className="h-px w-10 bg-amber-200/40" />
        <span className="text-[10px] uppercase tracking-[0.32em] text-amber-200/80 font-medium">
          Tonight's Pick
        </span>
        <span className="h-px w-10 bg-amber-200/40" />
      </div>

      {/* Poster stack — featured center, sharp + ringed; peeks behind on
          either side, smaller-feeling via rotation + opacity (but poster
          source itself is unchanged size for the featured). */}
      <div className="absolute inset-x-0 top-0 flex items-end justify-center pt-32 pointer-events-none">
        {peekLeft && (
          <div
            className="relative w-[26%] max-w-[120px] aspect-[2/3] rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.7)] -mr-10 opacity-60"
            style={{ transform: 'rotate(-9deg) translateY(28px)' }}
            aria-hidden="true"
          >
            <SmartImage fill src={peekLeft.poster} alt="" sizes="140px" className="h-full" />
            <div className="absolute inset-0 bg-velvet/30" />
          </div>
        )}
        <div className="relative w-[44%] max-w-[220px] aspect-[2/3] rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] z-10">
          <SmartImage fill src={movie.poster} alt={movie.title} sizes="240px" className="h-full" />
        </div>
        {peekRight && (
          <div
            className="relative w-[26%] max-w-[120px] aspect-[2/3] rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.7)] -ml-10 opacity-60"
            style={{ transform: 'rotate(9deg) translateY(28px)' }}
            aria-hidden="true"
          >
            <SmartImage fill src={peekRight.poster} alt="" sizes="140px" className="h-full" />
            <div className="absolute inset-0 bg-velvet/30" />
          </div>
        )}
      </div>

      {/* Title + meta + actions */}
      <div className="absolute bottom-6 left-0 right-0 px-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-200/80 mb-2">
          {movie.genres.slice(0, 2).map((g, i, arr) => (
            <span key={g} className="flex items-center gap-2">
              {g}
              {i < arr.length - 1 && <span>·</span>}
            </span>
          ))}
        </div>
        <h1 className="font-serif italic text-4xl text-white tracking-wide">{movie.title}</h1>
        <p className="mt-2 text-white/70 text-sm leading-relaxed line-clamp-2">{movie.synopsis}</p>
        <div className="mt-4 flex items-center gap-3">
          <Link
            data-testid="hero-play"
            to={`/movieflix/watch/${movie.id}`}
            className="flex-1 flex items-center justify-center gap-2 bg-white text-velvet px-4 py-3 rounded-xl font-medium text-sm active:scale-95 transition-transform"
          >
            <Play className="w-4 h-4 fill-current" /> Play
          </Link>
          <button
            data-testid="hero-add-list"
            data-in-list={inList}
            onClick={() => toggle(movie.id)}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm active:scale-95 transition-transform glass',
              inList && 'border-amber-200/40',
            )}
          >
            {inList ? <Check className="w-4 h-4 text-amber-200" /> : <Plus className="w-4 h-4" />}
            {inList ? 'In My List' : 'My List'}
          </button>
        </div>
      </div>
    </div>
  );
}
